<template>
  <div class="compensation-insight-tool-container">
    <SalaryCalculator v-if="!histogramRes || !columnarHeight" :bg-img="isMobile()
      ? 'https://video.reta-inc.com/image/default/91CD613F78E4479C84FF7AC5501A39B1-6-2.jpg'
      : 'https://video.reta-inc.com/image/default/0E7C90C27DD04A5A81804CC1D88FDDC1-6-2.jpg'
      " title="全球薪资洞察" desc="帮您了解全球员工市场薪酬，为你提供公平、有竞争力的市场报价">
      <div class="computer-container">
        <div class="title">全球薪酬洞察</div>
        <div class="tip">选择职位和国家，了解当地市场薪酬</div>
        <a-form class="form" ref="formRef" size="large" :model="formData" :label-col-props="{ span: 0 }"
          :wrapper-col-props="{ span: 24 }">
          <a-form-item field="jobTitle" :rules="[{ required: true, message: '职位为必选' }]" :validate-trigger="['change']"
            hide-asterisk>
            <a-select v-model="formData.jobTitle" placeholder="请选择职位" allow-search>
              <a-option v-for="job in salaryToolJobList" :key="job.value" :value="job.value">{{ job.label }}</a-option>
              <template #prefix>
                <div class="amount-prefix">职位</div>
              </template>
            </a-select>
          </a-form-item>
          <a-form-item field="countryCode" :rules="[{ required: true, message: '国家为必选' }]" :validate-trigger="['change']"
            hide-asterisk>
            <a-select v-model="formData.countryCode" placeholder="请选择国家" allow-search>
              <a-option v-for="c in salaryToolCountryList" :key="c.value" :value="c.value">{{ c.label }}</a-option>
              <template #prefix>
                <div class="amount-prefix">国家</div>
              </template>
            </a-select>
          </a-form-item>
        </a-form>
        <a-button class="btn" type="primary" :loading="btnLoading" @click="handleShow">查看</a-button>
      </div>
    </SalaryCalculator>
  </div>
</template>

<script setup lang="ts">
import { salaryToolCountryList, salaryToolJobList } from '@/common/global';
import { FormInstance } from '@arco-design/web-vue';
import { isMobile } from '@/common/utils';
import Cookies from 'js-cookie';

interface IFormData {
  jobTitle: string;
  countryCode: string;
  seniorityLevel: number;
}

interface IRes {
  confidence: string;
  country: string;
  currency: string;
  d1: number;
  d9: number;
  histogram: number[];
  jobTitle: string;
  median: number;
  scale: string;
  seniorityLevel: number;
  status: string;
}

const route = useRoute();
const leadsSuccess = useCookie('__leads_success__');
const viewCount = useCookie<number>('__view_count__');
const query = route.query;
const btnLoading = ref(false);
const columnarHeight = ref(0);
const calcTime = ref('year');
const formData = reactive<IFormData>({
  jobTitle: '',
  countryCode: '',
  seniorityLevel: 3,
});

if (leadsSuccess.value) {
  Cookies.remove('__view_count__');
} else {
  if (!viewCount.value) {
    Cookies.set('__view_count__', 1, { domain: 'dghire.com' });
  } else {
    Cookies.set('__view_count__', ++viewCount.value, { domain: 'dghire.com' });
  }
}

const seniorityLevelList = [
  {
    value: 1,
    text: '职位级别-初级',
  },
  {
    value: 2,
    text: '职位级别-中级',
  },
  {
    value: 3,
    text: '职位级别-高级',
  },
];

const histogramRes = ref<IRes | null>(null);
if (query.jobTitle && query.countryCode) {
  try {
    const { data } = await useAsyncData(async () => {
      const { data } = await useFetch<{ data: string }>('/api/resource/salary-insight', {
        method: 'POST',
        body: {
          jobTitle: query.jobTitle,
          countryCode: query.countryCode,
          seniorityLevel: query.seniorityLevel || 3,
        },
      });
      return { data: data.value?.data };
    });
    const res = JSON.parse(data.value?.data || '') as IRes;
    histogramRes.value = res;
  } catch (error) {
    console.info('获取数据失败');
  }
}

const gradeIndex = computed(() => {
  if (!histogramRes.value) {
    return [];
  }
  return [0, histogramRes.value.histogram.length - 1, histogramRes.value.histogram.findIndex(h => h === 1)];
});

const columnarBottomData = (index: number) => {
  const res = {
    salary: '',
    grade: '',
  };
  if (!histogramRes.value) {
    return res;
  }
  const { d1, d9, median } = histogramRes.value;
  let minWage = d1;
  let maxWage = d9;
  let medianWage = median;
  if (calcTime.value !== 'year') {
    minWage = Math.ceil(d1 / 12);
    maxWage = Math.ceil(d9 / 12);
    medianWage = Math.ceil(median / 12);
  }
  switch (index) {
    case 0:
      res.salary = minWage > 1000 ? parseInt(`${minWage / 1000}`) + 'K' : `${minWage}`;
      res.grade = '最低工资';
      break;
    case histogramRes.value?.histogram.length - 1:
      res.salary = maxWage > 1000 ? parseInt(`${maxWage / 1000}`) + 'K' : `${maxWage}`;
      res.grade = '最高工资';
      break;
    default:
      res.salary = medianWage > 1000 ? parseInt(`${medianWage / 1000}`) + 'K' : `${medianWage}`;
      res.grade = '中位数工资';
      break;
  }
  return res;
};

const formRef = ref<FormInstance | null>(null);
const handleShow = async () => {
  const res = await formRef.value?.validate();
  if (res) {
    return false;
  }
  const countryName = salaryToolCountryList.find(c => c.value === formData.countryCode)?.label;
  useLogger().track('compensation-insight-tool', {
    countryName,
    ...formData,
  });
  btnLoading.value = true;
  await navigateTo({
    path: `/resource/compensation-insight-tool-detail`,
    query: {
      ...route.query,
      ...formData,
    },
  });
  // window.location.reload();
};

const isFound = computed(() => {
  return histogramRes.value?.status === 'found';
});

const job = computed(() => salaryToolJobList.find(s => s.value === query.jobTitle)?.label);
const country = computed(() => salaryToolCountryList.find(s => s.value === query.countryCode)?.label);
const resTitle = computed(() => {
  return `${country.value}${job.value}薪酬`;
});

useSeoHead();



const tip = computed(() => {
  const levelMap: Record<number, string> = {
    1: '初级',
    2: '中级',
    3: '高级',
  };
  return `${country.value}${levelMap[Number(route.query.seniorityLevel) || 3]}【${job.value}】的${calcTime.value === 'year' ? '年薪' : '月薪'}中位数为${parseInt(
    (calcTime.value === 'year' ? histogramRes.value?.median : (histogramRes.value?.median || 0) / 12) + ''
  )}美元，工资估算是根据${country.value}【${job.value}】雇员匿名提交给DG.hire的资料，仅供参考`;
});

const isShowMask = computed(() => {
  return !leadsSuccess.value && viewCount.value && viewCount.value > 2;
});

onMounted(() => {
  formData.jobTitle = (route.query.jobTitle as string) || '';
  formData.countryCode = (route.query.countryCode as string) || '';
  formData.seniorityLevel = Number(route.query.seniorityLevel) || 3;
  columnarHeight.value = isMobile() ? 200 : 252;
});

const handleLeaveFunds = () => {
  useLeadsInfo('薪资洞察-留资解锁', 'unlock');
};
</script>

<style lang="less" scoped>
.compensation-insight-tool-container {
  padding-bottom: 66px;
  background-color: #f5f8ff;

  .computer-container {
    width: 680px;
    height: 350px;
    background: #ffffff;
    border-radius: 12px;
    margin: -50px auto 0px;
    overflow: hidden;

    .title {
      font-weight: 500;
      font-size: 24px;
      line-height: 34px;
      text-align: center;
      color: #1d2129;
      margin-top: 32px;
    }

    .form {
      width: 400px;
      margin: 0 auto;
      margin-top: 24px;

      :deep(.arco-row) {

        .arco-input-wrapper,
        .arco-select {
          background-color: #fff;
          border: 1px solid #c9cdd4;
          border-radius: 4px;
          padding: 0px 12px 0px 20px;

          .arco-input,
          .arco-select-view-input,
          .arco-select-view-value {
            font-size: 16px;

            &::placeholder {
              color: #86909c;
              font-size: 16px;
            }
          }

          .arco-select-view-prefix,
          .arco-input-prefix {
            padding-right: 43px;
            position: relative;

            .amount-prefix {
              font-weight: 400;
              font-size: 18px;
              line-height: 22px;
              color: #1d2129;
            }

            &::after {
              content: '';
              position: absolute;
              width: 1px;
              height: 20px;
              background-color: #e5e6eb;
              right: 23px;
            }
          }
        }
      }
    }

    .btn {
      width: 400px;
      height: 48px;
      margin: 12px auto;
      display: block;
      font-weight: 500;
      font-size: 16px;
      border-radius: 4px;
    }

    .tip {
      font-weight: 400;
      font-size: 16px;
      line-height: 22px;
      text-align: center;
      color: #4e5969;
      margin-top: 12px;
    }
  }

  .result-container {
    margin: 0 auto;
    width: fit-content;

    .main {
      display: flex;
      justify-content: center;

      .main-left {
        position: relative;
      }

      .main-right {
        margin-top: 24px;
        margin-left: 24px;
      }
    }

    .result-inner {
      width: 680px;
      height: 628px;
      box-sizing: border-box;
      background: linear-gradient(179.89deg, rgba(222, 255, 229, 0.2) 0.1%, rgba(188, 210, 255, 0) 6.08%), #ffffff;
      border: 1px solid #e8f0ff;
      border-radius: 8px;
      margin: 0 auto;
      margin-top: 24px;
      padding: 40px;

      .result-form {
        margin-top: 16px;

        :deep(.arco-select) {
          min-width: 160px;
          width: min-content;
          margin-right: 16px;
          background-color: #fff;
          border: 1px solid #e5e7eb;
          border-radius: 4px;
        }
      }

      .title {
        font-weight: 500;
        font-size: 24px;
        line-height: 34px;
        color: #1d2129;
      }

      .columnar-container {
        width: 100%;
        height: 370px;
        background: #ffffff;
        border-radius: 12px;
        margin-top: 48px;
        padding: 0px 40px 0;
        box-sizing: border-box;

        .columnar {
          display: flex;
          justify-content: space-between;
          align-items: flex-end;
          height: 252px;
          gap: 8px;

          .columnar-item {
            background: #d6e1ff;
            border-radius: 4px;
            width: -webkit-fill-available;
            transition: height 0.3s ease;
            position: relative;
            display: flex;
            justify-content: center;

            &.active {
              background: #4b63a5;
            }

            .desc {
              position: absolute;
              bottom: -90px;
              white-space: nowrap;

              .salary {
                font-weight: 500;
                font-size: 24px;
                line-height: 34px;
                text-align: center;
                color: #1d2129;
              }

              .grade {
                font-weight: 400;
                font-size: 20px;
                line-height: 28px;
                text-align: center;
                color: #4e5969;
                margin-top: 6px;
              }
            }
          }
        }

        .not-found {
          padding-top: 190px;
          overflow: hidden;
          overflow: hidden;
          height: 100%;
          background: url('@/assets/icons/salary-tool-not-found.svg') no-repeat center 54px/120px 120px;

          .not-found-tip {
            font-weight: 400;
            font-size: 20px;
            line-height: 32px;
            text-align: center;
            color: #86909c;
          }
        }
      }

      .tip {
        font-weight: 400;
        font-size: 12px;
        line-height: 16px;
        text-align: center;
        color: #86909c;
        padding: 0 60px;
        margin-top: 20px;
      }
    }

    .mask {
      position: absolute;
      bottom: -66px;
      left: 0;
      right: 0;
      height: 310px;
      background: linear-gradient(180deg, rgba(254, 254, 254, 0.18) 0%, #f2f2f2 100%);
      box-shadow: 0px -2px 32px rgba(0, 0, 0, 0.06);
      backdrop-filter: blur(8px);
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;

      .tip {
        margin: 12px 0;
      }

      .btn {
        width: 200px;
        height: 46px;
        font-weight: 500;
        font-size: 16px;
        line-height: 22px;
        color: #ffffff;
        border-radius: 8px;
      }
    }
  }
}

@media screen and (min-width: 800px) {
  .compensation-insight-tool-container {
    min-width: 1344px;
  }
}

@media screen and (max-width: 800px) {
  .compensation-insight-tool-container {
    width: 100%;
    padding-bottom: 32px;

    .computer-container {
      width: calc(100% - 32px);
      margin: -49px auto 0;
      padding: 0 16px 24px;
      box-sizing: border-box;
      height: auto;

      .title {
        font-size: 20px;
        line-height: 28px;
        margin-top: 24px;
      }

      .tip {
        font-weight: 400;
        font-size: 14px;
        line-height: 20px;
        margin-top: 8px;
      }

      .form {
        margin-top: 24px;
        width: 100%;

        :deep(.arco-form-item-content-wrapper) {

          .arco-select-view-single,
          .arco-input-wrapper {
            height: 48px;

            .arco-select-view-prefix,
            .arco-input-prefix {
              padding-right: 24px;

              &::after {
                right: 11px;
              }

              .amount-prefix {
                font-size: 16px;
              }
            }
          }
        }

        :deep(.arco-form-item) {
          margin-bottom: 16px;
        }
      }

      .btn {
        width: 100%;
        margin-top: 8px;
        margin-bottom: 0;
      }
    }

    .result-container {
      margin-top: 34px;
      padding: 0 16px;

      .result-inner {
        width: 100%;

        .title {
          font-weight: 500;
          font-size: 20px;
          line-height: 28px;
        }

        .result-form {
          :deep(.arco-select) {
            width: fit-content;
            min-width: unset;
          }
        }

        .columnar-container {
          margin-top: 16px;
          padding: 24px 22px 0;
          height: 300px;

          .columnar {
            height: 200px;

            .columnar-item {
              .desc {
                bottom: -50px;

                .salary {
                  font-weight: 500;
                  font-size: 16px;
                  line-height: 22px;
                }

                .grade {
                  font-weight: 400;
                  font-size: 12px;
                  line-height: 17px;
                  margin-top: 4px;
                }
              }
            }
          }
        }

        .tip {
          padding: 0;
          margin-top: 12px;
        }
      }

      .mask {
        height: 230px;
        bottom: -32px;

        .btn {
          font-size: 14px;
          height: 40px;
        }
      }
    }
  }
}
</style>
